import { Injectable } from '@angular/core';
import { Bitstream } from 'src/app/core/shared/bitstream.model';
import { DSONameService } from 'src/app/core/breadcrumbs/dso-name.service';
import { FileUtil } from './fileutil';
import { Fancybox } from '@fancyapps/ui';
import { hasValue } from '../empty.util';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GalleryService {
  private items: object[] = [];

  private appended = 0;
  private youtubeVideos = 0;
  private loadingCount = 0;
  private originalCount = 0;

  constructor( private dsoNameService: DSONameService, private http: HttpClient ) { }

  public startLoading(): void {
    this.loadingCount++;
  }

  public finishLoading(): void {
    this.loadingCount--;
  }

  public getIndexForBundle( bundle: string, idx: number ): number {
    if ( bundle === 'PRESERVATION' ) {
      return this.youtubeVideos + this.originalCount + idx;
    } else {
      return this.youtubeVideos + idx;
    }
  }

  /// Open the gallery
  public open( idx?: number ): void{
    /// Don't open the gallery while some items are still being loaded
    if (this.loadingCount > 0){
      return;
    }
    this.close();
    if (this.items.length > 0 ){
      if ( hasValue( idx ) && ( idx < this.items.length ) ) {
        Fancybox.show( this.items, { startIndex: idx } );
      } else {
        Fancybox.show( this.items );
      }
    }
  }

  /// Close the gallery
  public close(): void{
    Fancybox.close();
  }

  /// Clears the items from the gallery item list
  public clearItems(): void {
    this.items = [];
    this.appended = 0;
    this.originalCount = 0;
    this.youtubeVideos = 0;
  }

  /// Check if the item has a downloadable thumbnail, if it has add the thumbnail to the gallery
  private checkThumbnail( url: string, idx: number ): void {
    /// Make sure the gallery will not open until loading is completed
    this.startLoading();

    /// Check if there's a file with this Url
    this.http.head( url, { observe: 'response' } )
    .pipe(
      catchError( () => of( null ) )
    )
    .subscribe( res => {
      if ( ( res !== null ) && ( res.status === 200 ) ) {
        /// DSpace returns a HTML page on 404, and a JSON type response of success
        let contentType: string = res.headers.get( 'Content-Type' );
        if ( contentType.toLocaleLowerCase().startsWith('application/json') ) {
          let item: object = this.items[ idx ];
          item[ 'thumb' ] = url;
        }
      }
        this.finishLoading();
      });
  }

  /// Adds more items to the gallery item list
  public appendItem( itemHandle: string, bitstream: Bitstream ): void{
    let fileName: string = this.dsoNameService.getName( bitstream );
    let path: string = '/bitstream/handle/' + itemHandle + '/' + fileName;
    let thumbnail: string = path + '.jpg';
    let item: object = null;
    if ( FileUtil.isImage( fileName ) ) {
      item = { src: path, caption: fileName };
    } else
    if ( FileUtil.isVideo( fileName ) ) {
      item = { src: path, thumb: 'assets/dspace/images/video.jpg', videoAutoplay: false, caption: fileName };
    } else
    if ( FileUtil.isAudio( fileName ) ) {
      item = { src: '<audio controls><source src="' + path + '" type="audio/mpeg"></audio>', type: 'html', thumb: 'assets/dspace/images/audio.jpg', caption: fileName };
    }

    if ( item !== null ) {
      this.items.push( item );
      this.checkThumbnail( thumbnail, this.appended );
      this.appended++;
    }
  }

  public addItem( bundle: string, itemHandle: string, bitstream: Bitstream ): void {
    if ( bundle === 'ORIGINAL' ) {
      this.originalCount++;
    }
    this.appendItem( itemHandle, bitstream );
  }

  public addYoutubeVideo( url: string ): void {
    this.items.push( { src: url, thumb: 'assets/dspace/images/video.jpg', videoAutoplay: false } );
    this.youtubeVideos++;
  }
}
